/** Device-local listening policy. Collapsible preferences set defaults; manual toggles take precedence. */
export interface TTSSettings {
  /** Pause a performance to narrate its timed commentary, then resume its owner. */
  readVideoEvents: boolean;
  includeFootnotes: boolean;
  autoExpandPocketConcerts: boolean;
  autoExpandRabbitHoles: boolean;
  autoExpandOtherCollapsibleSections: boolean;
  /** Play linked YouTube performances immediately after their spoken labels, then resume the prose. */
  includeInlineLinks: boolean;
  /** Seconds from the linked start timestamp; null means no additional time limit. */
  maxLinkedVideoSeconds: number | null;
  /** Start new listening visits at variant zero; manual choice continues through the remaining variants. */
  playAllVideosInSwitcher: boolean;
}

export const DEFAULT_TTS_SETTINGS: TTSSettings = {
  readVideoEvents: false,
  includeFootnotes: true,
  autoExpandPocketConcerts: true,
  autoExpandRabbitHoles: false,
  autoExpandOtherCollapsibleSections: true,
  includeInlineLinks: false,
  maxLinkedVideoSeconds: 15,
  playAllVideosInSwitcher: false,
};

export const LINKED_VIDEO_SECONDS_RANGE = { min: 5, max: 120, step: 1, unlimited: null } as const;

export const TTS_SETTING_OPTIONS: { key: Exclude<keyof TTSSettings, 'maxLinkedVideoSeconds'>; label: string; description: string }[] = [
  { key: 'includeFootnotes', label: 'Include Footnotes', description: 'Read footnotes along with the chapter.' },
  { key: 'autoExpandPocketConcerts', label: 'Expand Pocket Concerts by default', description: 'Open Pocket Concerts when listening enters. You can still open or close each section.' },
  { key: 'autoExpandRabbitHoles', label: 'Expand Rabbit Holes by default', description: 'Open Rabbit Holes when listening enters. You can still open or close each section.' },
  { key: 'autoExpandOtherCollapsibleSections', label: 'Expand other sections by default', description: 'Open other expandable sections when listening enters. Your manual choices take precedence.' },
  { key: 'includeInlineLinks', label: 'Include inline links', description: 'Play linked videos after their link text is read.' },
  { key: 'playAllVideosInSwitcher', label: 'Play all videos in a switcher', description: 'Continue through the remaining videos before moving on.' },
  { key: 'readVideoEvents', label: 'Read video events', description: 'Pause to read timed commentary, then resume the video.' },
];

/** Older and partial saved preferences inherit the current defaults. */
export function readTTSSettings(value: unknown): TTSSettings {
  const settings = { ...DEFAULT_TTS_SETTINGS };
  if (!value || typeof value !== 'object') return settings;
  for (const { key } of TTS_SETTING_OPTIONS) {
    if (key in value && typeof value[key] === 'boolean') settings[key] = value[key];
  }
  const legacyGroupSettings = [
    ['includePocketConcerts', 'autoExpandPocketConcerts'],
    ['includeRabbitHoles', 'autoExpandRabbitHoles'],
    ['includeOtherCollapsibleSections', 'autoExpandOtherCollapsibleSections'],
  ] as const;
  for (const [legacy, current] of legacyGroupSettings) {
    if (!(current in value) && legacy in value && typeof value[legacy] === 'boolean') {
      settings[current] = value[legacy];
    }
  }
  if ('maxLinkedVideoSeconds' in value) {
    const seconds = value.maxLinkedVideoSeconds;
    if (seconds === null) settings.maxLinkedVideoSeconds = null;
    // Migrate longer saved choices from the former ranges to Unlimited.
    else if (typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= LINKED_VIDEO_SECONDS_RANGE.min && seconds <= 600) {
      settings.maxLinkedVideoSeconds = seconds > LINKED_VIDEO_SECONDS_RANGE.max ? null : Math.round(seconds);
    }
  }
  return settings;
}

export function formatLinkedVideoTime(seconds: number | null): string {
  if (seconds === null) return 'Unlimited';
  if (seconds < 60) return `${seconds} sec`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes} min${remainder ? ` ${remainder} sec` : ''}`;
}
